import {ref, computed, onMounted, onUnmounted, nextTick} from 'vue';

/**
 * Composable for making elements draggable
 * @param {Object} options - Configuration options
 * @param {Object} options.initialPosition - Initial position { bottom, right } or { top, right, transform }
 * @param {Boolean} options.enabled - Whether dragging is enabled
 * @param {String} options.storageKey - LocalStorage key for position persistence
 * @param {Number} options.snapThreshold - Distance threshold for edge snapping
 * @param {Number} options.boundaryPadding - Minimum distance from viewport edges
 * @returns {Object} Draggable state and methods
 */
export function useDraggable(options = {}) {
  const {
    initialPosition = {top: '50%', right: 0, transform: 'translateY(-50%)'},
    enabled = true,
    storageKey = 'draggablePosition',
    snapThreshold = 50,
    boundaryPadding = 10,
    onDragEnd = null,
  } = options;

  // Reactive state
  const elementRef = ref(null);
  const position = ref({...initialPosition});
  const isDragging = ref(false);
  const dragOffset = ref({x: 0, y: 0});
  const elementDimensions = ref({width: 0, height: 0});
  const hasMoved = ref(false);

  // Computed style for positioning
  const positionStyle = computed(() => {
    const style = {
      position: 'fixed',
      right: typeof position.value.right === 'number' ? `${position.value.right}px` : position.value.right,
    };

    // Support both bottom positioning and top positioning with transform
    if (position.value.top !== undefined) {
      style.top = typeof position.value.top === 'number' ? `${position.value.top}px` : position.value.top;
      if (position.value.transform) {
        style.transform = position.value.transform;
      }
    } else if (position.value.bottom !== undefined) {
      style.bottom = typeof position.value.bottom === 'number' ? `${position.value.bottom}px` : position.value.bottom;
    }

    return style;
  });

  // Update element dimensions
  const updateElementDimensions = () => {
    if (elementRef.value) {
      const rect = elementRef.value.getBoundingClientRect();
      elementDimensions.value = {
        width: rect.width,
        height: rect.height,
      };
    }
  };

  // Mouse down handler for drag start
  const startDrag = e => {
    if (!enabled) return;

    // Prevent text selection and default behavior
    e.preventDefault();
    e.stopPropagation();

    isDragging.value = true;
    hasMoved.value = false; // Reset movement flag

    // Calculate offset from mouse to element
    const rect = elementRef.value.getBoundingClientRect();
    dragOffset.value = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };

    // Update element dimensions
    updateElementDimensions();

    // Add global event listeners
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
  };

  // Mouse move handler for dragging
  const onMouseMove = e => {
    if (!isDragging.value) return;

    hasMoved.value = true; // Mark that we actually moved

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const {width: elementWidth, height: elementHeight} = elementDimensions.value;

    // Calculate new position based on mouse position
    const newLeft = e.clientX - dragOffset.value.x;
    const newTop = e.clientY - dragOffset.value.y;

    // Convert to right coordinate
    const newRight = viewportWidth - (newLeft + elementWidth);

    // Apply boundary constraints for right position
    position.value.right = Math.max(0, Math.min(newRight, viewportWidth - elementWidth - boundaryPadding));

    // Handle vertical positioning based on current position type
    if (position.value.top !== undefined) {
      // For top positioning, constrain within viewport
      position.value.top = Math.max(
        boundaryPadding,
        Math.min(newTop, viewportHeight - elementHeight - boundaryPadding),
      );
      // Remove transform when dragging to use absolute positioning
      delete position.value.transform;
    } else {
      // Convert to bottom coordinates for bottom positioning
      const newBottom = viewportHeight - (newTop + elementHeight);
      position.value.bottom = Math.max(
        boundaryPadding,
        Math.min(newBottom, viewportHeight - elementHeight - boundaryPadding),
      );
    }
  };

  // Mouse up handler for drag end
  const onMouseUp = () => {
    if (!isDragging.value) return;

    isDragging.value = false;

    // Remove global event listeners
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';

    // Apply snap to edges if close enough
    snapToEdges();

    // Save position to localStorage
    savePosition();

    // Call onDragEnd callback if provided and we actually moved
    if (onDragEnd && typeof onDragEnd === 'function' && hasMoved.value) {
      onDragEnd();
    }
  };

  // Snap to edges when close
  const snapToEdges = () => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const {width: elementWidth, height: elementHeight} = elementDimensions.value;

    // Snap to right edge
    if (position.value.right < snapThreshold) {
      position.value.right = 0;
    }

    // Snap to left edge
    if (position.value.right > viewportWidth - elementWidth - snapThreshold) {
      position.value.right = viewportWidth - elementWidth - 20;
    }

    // Handle vertical snapping based on position type
    if (position.value.top !== undefined) {
      // For top positioning
      if (position.value.top < snapThreshold) {
        position.value.top = 20;
      }
      if (position.value.top > viewportHeight - elementHeight - snapThreshold) {
        position.value.top = viewportHeight - elementHeight - 20;
      }
    } else if (position.value.bottom !== undefined) {
      // For bottom positioning
      if (position.value.bottom < snapThreshold) {
        position.value.bottom = 20;
      }
      if (position.value.bottom > viewportHeight - elementHeight - snapThreshold) {
        position.value.bottom = viewportHeight - elementHeight - 20;
      }
    }
  };

  // Save position to localStorage
  const savePosition = () => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(position.value));
    } catch (error) {
      console.warn('Failed to save position to localStorage:', error);
    }
  };

  // Load position from localStorage
  const loadPosition = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const savedPosition = JSON.parse(saved);
        position.value = {...position.value, ...savedPosition};

        // Validate position is still within viewport
        validatePosition();
      }
    } catch (error) {
      console.warn('Failed to load position from localStorage:', error);
    }
  };

  // Validate position is within viewport bounds
  const validatePosition = () => {
    nextTick(() => {
      updateElementDimensions();

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const {width: elementWidth, height: elementHeight} = elementDimensions.value;

      // Ensure popup stays within viewport - right position
      position.value.right = Math.max(
        0,
        Math.min(position.value.right, viewportWidth - elementWidth - boundaryPadding),
      );

      // Handle vertical positioning validation
      if (position.value.top !== undefined && typeof position.value.top === 'number') {
        // For numeric top positioning
        position.value.top = Math.max(
          boundaryPadding,
          Math.min(position.value.top, viewportHeight - elementHeight - boundaryPadding),
        );
      } else if (position.value.bottom !== undefined) {
        // For bottom positioning
        position.value.bottom = Math.max(
          boundaryPadding,
          Math.min(position.value.bottom, viewportHeight - elementHeight - boundaryPadding),
        );
      }
      // For percentage-based top positioning with transform, no validation needed
    });
  };

  // Handle window resize
  const handleResize = () => {
    validatePosition();
  };

  // Reset position to default
  const resetPosition = () => {
    position.value = {...initialPosition};
    savePosition();
  };

  // Setup and cleanup
  const setup = () => {
    loadPosition();
    window.addEventListener('resize', handleResize);

    // Update dimensions after mount
    nextTick(() => {
      updateElementDimensions();
      validatePosition();
    });
  };

  const cleanup = () => {
    window.removeEventListener('resize', handleResize);

    // Clean up drag listeners if still active
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  };

  // Auto setup/cleanup when used in component
  onMounted(setup);
  onUnmounted(cleanup);

  return {
    // Refs
    elementRef,
    position,
    isDragging,
    elementDimensions,

    // Computed
    positionStyle,

    // Methods
    startDrag,
    resetPosition,
    savePosition,
    loadPosition,
    validatePosition,
    updateElementDimensions,

    // Manual setup/cleanup for advanced usage
    setup,
    cleanup,
  };
}
